import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Save } from "lucide-react";
import {
  AppEditor,
  CertificationsEditor,
  ContactEditor,
  CropsEditor,
  EnquiriesList,
  FittingsEditor,
  HeroEditor,
  ManifestoEditor,
  ProductsEditor,
  StatsEditor,
  StoriesEditor,
  TeamEditor,
  UpdatesEditor,
} from "./editors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { key: "enquiries", label: "Enquiries" },
  { key: "hero", label: "Hero" },
  { key: "whyjamna", label: "Why Jamna" },
  { key: "certifications", label: "Certifications" },
  { key: "stats", label: "Stats" },
  { key: "products", label: "Products" },
  { key: "fittings", label: "Fittings" },
  { key: "team", label: "Team" },
  { key: "app", label: "App / APK" },
  { key: "stories", label: "Stories" },
  { key: "updates", label: "Updates" },
  { key: "crops", label: "Crop Guide" },
  { key: "contact", label: "Contact" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("jamna_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/auth/me`, auth)
      .then(() => axios.get(`${API}/content`))
      .then(({ data }) => setContent(data))
      .catch(() => {
        localStorage.removeItem("jamna_admin_token");
        navigate("/admin/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content, auth);
      toast.success("Saved! Website turant update ho gayi.");
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("jamna_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Save nahi hua. Dobara try karein.");
      }
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("jamna_admin_token");
    navigate("/admin/login");
  };

  if (!content) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-forest text-sm uppercase tracking-[0.2em] text-sage">
        Loading…
      </div>
    );
  }

  const setSlice = (key) => (val) => setContent((cc) => ({ ...cc, [key]: val }));

  return (
    <div className="min-h-screen bg-forest font-body text-cream" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-line bg-forest/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <p className="font-display text-lg font-bold tracking-tight">
            JAMNA <span className="italic text-harvest">ADMIN</span>
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="text-xs uppercase tracking-[0.15em] text-sage transition-colors duration-300 hover:text-harvest"
            >
              View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-line px-4 py-2 text-xs uppercase tracking-[0.15em] text-sage transition-colors duration-300 hover:border-harvest hover:text-harvest"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1200px] gap-8 px-6 py-10 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="flex flex-wrap gap-2 lg:flex-col" data-testid="admin-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`px-4 py-3 text-left text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tab === t.key
                    ? "bg-harvest font-semibold text-forest"
                    : "border border-line text-sage hover:border-harvest hover:text-harvest"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </aside>

        <div className="lg:col-span-9">
          {tab === "enquiries" && <EnquiriesList />}
          {tab === "hero" && <HeroEditor value={content.hero} onChange={setSlice("hero")} />}
          {tab === "whyjamna" && <ManifestoEditor value={content.manifesto || {}} onChange={setSlice("manifesto")} />}
          {tab === "certifications" && <CertificationsEditor value={content.certifications || []} onChange={setSlice("certifications")} />}
          {tab === "stats" && <StatsEditor value={content.stats || []} onChange={setSlice("stats")} />}
          {tab === "products" && <ProductsEditor value={content.products} onChange={setSlice("products")} />}
          {tab === "stories" && <StoriesEditor value={content.stories} onChange={setSlice("stories")} />}
          {tab === "updates" && <UpdatesEditor value={content.updates} onChange={setSlice("updates")} />}
          {tab === "crops" && <CropsEditor value={content.crops} onChange={setSlice("crops")} />}
          {tab === "contact" && <ContactEditor value={content.contact} onChange={setSlice("contact")} />}
          {tab === "fittings" && (
            <FittingsEditor value={content.fittings || []} onChange={setSlice("fittings")} />
          )}
          {tab === "team" && <TeamEditor value={content.team || []} onChange={setSlice("team")} />}
          {tab === "app" && (
            <AppEditor
              value={content.app || { name: "", version: "", desc: "", apkUrl: "" }}
              onChange={setSlice("app")}
            />
          )}

          {tab !== "enquiries" && (
            <div className="sticky bottom-0 mt-8 border-t border-line bg-forest py-4">
              <button
                onClick={save}
                disabled={saving}
                data-testid="admin-save-button"
                className="inline-flex w-full items-center justify-center gap-2 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-harvestHover disabled:opacity-60"
              >
                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
